import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { create } from 'zustand';

export type InAppHistoryEntry = {
  /** react-router's `location.key`: one per browser history entry. */
  key: string;
  pathname: string;
};

type InAppHistoryState = {
  /** The pages visited in this tab, oldest first; the last one is the current page. */
  entries: InAppHistoryEntry[];
  record: (entry: InAppHistoryEntry, type: 'PUSH' | 'REPLACE' | 'POP') => void;
};

/** How many pages are kept. Recent pages shows 5, the rest only lets Back step further. */
const MAX_ENTRIES = 50;

/**
 * The in-app history behind the top bar's Back button. It mirrors the browser history of this tab
 * from the first page the app opened on, so Back is disabled until you have moved between pages, and
 * stepping back `n` entries is `navigate(-n)`.
 */
export const useInAppHistoryStore = create<InAppHistoryState>((set) => ({
  entries: [],
  record: (entry, type) =>
    set(({ entries }) => {
      const last = entries.at(-1);
      if (last?.key === entry.key) return { entries };
      if (type === 'REPLACE' && last) return { entries: [...entries.slice(0, -1), entry] };
      if (type === 'POP') {
        // Browser Back/Forward or navigate(-n): back to an entry we know, or a forward step.
        const index = entries.findIndex((e) => e.key === entry.key);
        if (index >= 0) return { entries: entries.slice(0, index + 1) };
      }
      return { entries: [...entries, entry].slice(-MAX_ENTRIES) };
    }),
}));

/** Feeds every navigation into the in-app history. Mount once, inside the router. */
export const useTrackInAppHistory = () => {
  const { key, pathname } = useLocation();
  const type = useNavigationType();
  const record = useInAppHistoryStore((state) => state.record);

  useEffect(() => {
    record({ key, pathname }, type);
  }, [key, pathname, type, record]);
};
